import {
  Autocomplete,
  Box,
  Button,
  Chip,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import { actionValidation } from '../../utils/energyTreeFunctions';
import { useTheme } from '@emotion/react';
import { useMemo, useState } from 'react';

import { usePutEnergyTreeNodesMutation } from '../../../../api/recalcV2/endpoints/energyTreeNodesApiSlice';

// * Redux
import { showSnackbar } from '@/redux/snackbarSlice';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { useDispatch, useSelector } from 'react-redux';

const MoveNodeTab = ({
  clickedNode,
  treeOptions,
  handleClose,
  tree,
  isMainMeterUsed,
}) => {
  const theme = useTheme();
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();

  const [parentId, setParentId] = useState(null);

  const [putEnergyTreeNodes] = usePutEnergyTreeNodesMutation();

  const [hoveredPath, setHoveredPath] = useState([]);

  const validation = useMemo(() => {
    if (!hoveredPath.length || !clickedNode) return { valid: true };

    const newParent = hoveredPath[hoveredPath.length - 1];
    return actionValidation({
      action: 'move',
      targetType: clickedNode?.type,
      currentNode: clickedNode,
      parent: newParent,
      tree: tree,
      isMainMeterUsed: isMainMeterUsed,
    });
  }, [hoveredPath, clickedNode, tree, isMainMeterUsed]);

  const handleUpdateNode = async () => {
    const payload = {
      ...clickedNode,
      parentId: parentId,
    };

    try {
      await putEnergyTreeNodes({
        accountId: account.AccountId,
        energyTreeNodeId: clickedNode.energyTreeNodeId,
        updatedEnergyTreeNode: payload,
      }).unwrap();
      dispatch(
        showSnackbar({ message: 'Node opdateret', severity: 'success' }),
      );
      handleClose();
    } catch (error) {
      console.error('Failed to update node:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i opdatering af node',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  return (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight={600}>
          {clickedNode?.name || 'Node Details'}
        </Typography>
        <Box
          sx={{
            backgroundColor: getTypeColor(clickedNode?.type),
            borderRadius: '25px',
            width: '40px',
            height: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: `1px solid divider`,
          }}
        >
          <i
            className={`fa-solid ${getTypeIcon(clickedNode?.type)}`}
            style={{ color: 'white' }}
          ></i>
        </Box>
      </Box>

      <Autocomplete
        id="parent-node-selector"
        fullWidth
        options={treeOptions}
        getOptionLabel={(option) => option.name}
        onChange={(event, newValue) => {
          setParentId(newValue ? newValue.energyTreeNodeId : null);
        }}
        groupBy={() => 'single-group'}
        filterOptions={(options, { inputValue }) => {
          const lowerInput = inputValue.toLowerCase();
          return options.filter((option) =>
            option._path?.some((n) =>
              n.name.toLowerCase().includes(lowerInput),
            ),
          );
        }}
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader sx={{ bgcolor: 'transparent' }}>
              <Chip
                variant="filled"
                size="large"
                sx={{
                  backgroundColor:
                    theme.palette.mode === 'dark' ? '#121212' : '#FFFFFF',
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                }}
                label={
                  <Box component="span">
                    {hoveredPath.map((n) => n.name).join(' / ')}
                    {clickedNode?.name && (
                      <Box
                        component="span"
                        sx={{
                          color: validation.isValid ? '#4CAF50' : '#f44336',
                          fontWeight: 600,
                          ml: 0.5,
                        }}
                      >
                        {validation.isValid ? '+ /' : '%'} {clickedNode.name}
                      </Box>
                    )}
                  </Box>
                }
              />
            </ListSubheader>
            {params.children}
          </li>
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.energyTreeNodeId}
            onMouseEnter={() => setHoveredPath(option._path || [])}
            style={{ paddingLeft: `${option._path?.length * 24}px` }}
          >
            <Box
              sx={{
                backgroundColor: getTypeColor(option?.type),
                position: 'relative', // Changed from 'absolute' to maintain layout
                borderRadius: '25px',
                width: '28px',
                height: '28px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border: `1px solid divider`,
                marginRight: 1,
              }}
            >
              <i
                style={{
                  color: 'white',
                }}
                className={`fa-solid ${getTypeIcon(option?.type)}`}
              ></i>
            </Box>
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Vælg node som ny forælder"
            placeholder="Vælg node som ny forælder"
            variant="filled"
            helperText={
              validation.isValid
                ? 'Vælg en node, som du vil flytte denne node under.'
                : validation.invalidReason
            }
            error={!validation.isValid}
            sx={{
              '& .MuiFilledInput-root': {
                '&:before': { borderBottom: 'none' },
                '&:after': { borderBottom: 'none' },
                '&:hover:before': { borderBottom: 'none !important' },
                borderRadius: 1,
              },
            }}
          />
        )}
      />

      <Box display="flex" justifyContent="end" mt={2}>
        <Button
          variant="contained"
          onClick={handleUpdateNode}
          disabled={!validation.isValid || parentId === null}
        >
          Flyt Node
        </Button>
      </Box>
    </Stack>
  );
};

export default MoveNodeTab;
